import { Navigate, Outlet } from "react-router-dom";
import { useAppSelector } from "@/app/hooks";
import { useAnyPermission } from "@/lib/permissions";
import type { Permission } from "@/lib/permissions";

interface Props {
  /** One permission, or a list of which any one is enough. */
  permission: Permission | readonly Permission[];
  /** The account owner only, whatever rights a sub-admin holds. */
  ownerOnly?: boolean;
  children?: React.ReactNode;
}

/**
 * Route-level permission gate.
 *
 * Sends anyone without the permission back to the dashboard rather than
 * rendering a "no access" panel: a section this person cannot use is simply
 * not part of their panel, the same as it is missing from their nav.
 *
 * This is convenience only. The server re-checks every call, and a user who
 * bypasses this gate reaches an API that refuses them.
 */
export function RequirePermission({ permission, ownerOnly = false, children }: Props) {
  const required = typeof permission === "string" ? [permission] : permission;
  const isOwner = useAppSelector((state) => state.session.is_owner);
  const allowed = useAnyPermission(...required) && (!ownerOnly || isOwner);

  if (!allowed) return <Navigate to="/" replace />;

  return children ? <>{children}</> : <Outlet />;
}
